import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Fab,
  Tab,
  Tabs
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Publish,
  Unpublished,
  LibraryBooks,
  PlayLesson,
  Quiz,
  Description,
  VideoLibrary,
  Refresh,
  Save,
  Cancel,
  CheckCircle,
  Assessment
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

function AuthorDashboard() {
  const { user, logout } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseModules, setCourseModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleContent, setModuleContent] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  // Диалоги
  const [createCourseDialog, setCreateCourseDialog] = useState(false);
  const [editCourseDialog, setEditCourseDialog] = useState(false);
  const [createModuleDialog, setCreateModuleDialog] = useState(false);
  const [editModuleDialog, setEditModuleDialog] = useState(false);
  const [createContentDialog, setCreateContentDialog] = useState(false);
  const [createTestDialog, setCreateTestDialog] = useState(false);
  const [editTestDialog, setEditTestDialog] = useState(false);

  // Формы
  const [courseForm, setCourseForm] = useState({ title: '', description: '' });
  const [moduleForm, setModuleForm] = useState({ title: '', description: '' });
  const [contentForm, setContentForm] = useState({
    contentType: 'text',
    title: '',
    content: ''
  });
  const [editContentDialog, setEditContentDialog] = useState(false);
  const [contentEditForm, setContentEditForm] = useState({
    contentType: 'text',
    title: '',
    content: ''
  });
  const [testForm, setTestForm] = useState({
    title: '',
    description: '',
    passingScore: 70
  });

  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    answers: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ]
  });

  // Данные
  const [moduleTest, setModuleTest] = useState(null);
  const [testQuestions, setTestQuestions] = useState([]);
  const [allTests, setAllTests] = useState([]);

  const [file, setFile] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [selectedContentItem, setSelectedContentItem] = useState(null);

  // Отчеты
  const [courseReport, setCourseReport] = useState(null);
  const [reportDialog, setReportDialog] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/courses');
      setCourses(response.data);
      // Также загружаем все тесты автора
      await loadAllTests();
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllTests = async () => {
    try {
      const response = await api.get('/tests');
      setAllTests(response.data);
    } catch (error) {
      console.error('Error loading tests:', error);
      setAllTests([]);
    }
  };

  const loadCourseReport = async (courseId) => {
    try {
      setLoadingReport(true);
      console.log('=== LOADING COURSE REPORT ===', courseId);
      const params = {};
      if (reportStartDate) params.startDate = reportStartDate;
      if (reportEndDate) params.endDate = reportEndDate;
      const response = await api.get(`/courses/${courseId}/students`, { params });
      console.log('=== COURSE REPORT RESPONSE ===', response.data);
      setCourseReport(response.data);
      setReportDialog(true);
    } catch (error) {
      console.error('Error loading course report:', error);
      alert('Ошибка при загрузке отчета');
    } finally {
      setLoadingReport(false);
    }
  };

  const loadCourseModules = async (courseId) => {
    try {
      const response = await api.get(`/courses/${courseId}/modules`);
      setCourseModules(response.data);
    } catch (error) {
      console.error('Error loading modules:', error);
    }
  };

  const loadModuleContent = async (courseId, moduleId) => {
    try {
      const response = await api.get(`/courses/${courseId}/modules/${moduleId}/content`);
      setModuleContent(response.data);
    } catch (error) {
      console.error('Error loading content:', error);
    }
  };

  const handleOpenEditContent = (content) => {
    setSelectedContentItem(content);
    setContentEditForm({
      contentType: content.content_type,
      title: content.title,
      content: content.content || ''
    });
    setEditFile(null);
    setEditContentDialog(true);
  };

  const handleUpdateContent = async () => {
    if (!selectedCourse || !selectedModule || !selectedContentItem) return;
    try {
      const formData = new FormData();
      Object.keys(contentEditForm).forEach(key => {
        formData.append(key, contentEditForm[key]);
      });
      if (editFile) {
        formData.append('file', editFile);
      }
      await api.put(
        `/courses/${selectedCourse.id}/modules/${selectedModule.id}/content/${selectedContentItem.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setEditContentDialog(false);
      setSelectedContentItem(null);
      setEditFile(null);
      await loadModuleContent(selectedCourse.id, selectedModule.id);
    } catch (error) {
      console.error('Error updating content:', error);
      alert('Ошибка при обновлении контента');
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!selectedCourse || !selectedModule) return;
    if (!window.confirm('Удалить этот элемент контента? Действие необратимо.')) return;
    try {
      await api.delete(`/courses/${selectedCourse.id}/modules/${selectedModule.id}/content/${contentId}`);
      await loadModuleContent(selectedCourse.id, selectedModule.id);
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Ошибка при удалении контента');
    }
  };

  const handleCreateCourse = async () => {
    try {
      await api.post('/courses', courseForm);
      setCreateCourseDialog(false);
      setCourseForm({ title: '', description: '' });
      loadCourses();
    } catch (error) {
      console.error('Error creating course:', error);
    }
  };

  const handleUpdateCourse = async () => {
    try {
      await api.put(`/courses/${selectedCourse.id}`, courseForm);
      setEditCourseDialog(false);
      setCourseForm({ title: '', description: '' });
      setSelectedCourse(null);
      loadCourses();
    } catch (error) {
      console.error('Error updating course:', error);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот курс?')) {
      try {
        await api.delete(`/courses/${courseId}`);
        loadCourses();
        if (selectedCourse?.id === courseId) {
          setSelectedCourse(null);
          setCourseModules([]);
          setSelectedModule(null);
          setModuleContent([]);
        }
      } catch (error) {
        console.error('Error deleting course:', error);
      }
    }
  };

  const handleTogglePublish = async (courseId, isPublished) => {
    try {
      await api.put(`/courses/${courseId}`, { isPublished: !isPublished });
      loadCourses();
    } catch (error) {
      console.error('Error toggling publish status:', error);
    }
  };

  const handleCreateModule = async () => {
    try {
      await api.post(`/courses/${selectedCourse.id}/modules`, moduleForm);
      setCreateModuleDialog(false);
      setModuleForm({ title: '', description: '' });
      loadCourseModules(selectedCourse.id);
    } catch (error) {
      console.error('Error creating module:', error);
    }
  };

  const handleUpdateModule = async () => {
    try {
      await api.put(`/courses/${selectedCourse.id}/modules/${selectedModule.id}`, moduleForm);
      setEditModuleDialog(false);
      setModuleForm({ title: '', description: '' });
      setSelectedModule(null);
      loadCourseModules(selectedCourse.id);
    } catch (error) {
      console.error('Error updating module:', error);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот модуль?')) {
      try {
        await api.delete(`/courses/${selectedCourse.id}/modules/${moduleId}`);
        loadCourseModules(selectedCourse.id);
        if (selectedModule?.id === moduleId) {
          setSelectedModule(null);
          setModuleContent([]);
        }
      } catch (error) {
        console.error('Error deleting module:', error);
      }
    }
  };

  const handleDeleteTest = async (testId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот тест? Все вопросы и результаты тестирования будут удалены.')) {
      try {
        await api.delete(`/tests/${testId}`);
        setModuleTest(null);
        setTestQuestions([]);
        loadModuleTest(selectedModule.id);
      } catch (error) {
        console.error('Error deleting test:', error);
        alert('Ошибка при удалении теста');
      }
    }
  };

  const handleCreateContent = async () => {
    try {
      const formData = new FormData();

      Object.keys(contentForm).forEach(key => {
        formData.append(key, contentForm[key]);
      });

      if (file) {
        formData.append('file', file);
      }

      await api.post(`/courses/${selectedCourse.id}/modules/${selectedModule.id}/content`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setCreateContentDialog(false);
      setContentForm({ contentType: 'text', title: '', content: '' });
      setFile(null);
      loadModuleContent(selectedCourse.id, selectedModule.id);
    } catch (error) {
      console.error('Error creating content:', error);
    }
  };

  const handleCreateTest = async () => {
    try {
      console.log('=== CREATING TEST FROM FRONTEND ===');
      console.log('Test form:', testForm);
      console.log('Selected module:', selectedModule);

      await api.post('/tests', {
        title: testForm.title,
        description: testForm.description,
        passing_score: testForm.passingScore,
        module_id: selectedModule.id
      });
      setCreateTestDialog(false);
      setTestForm({ title: '', description: '', passingScore: 70 });
      console.log('Test created successfully');
      // Перезагружаем тест модуля
      if (selectedModule) {
        loadModuleTest(selectedModule.id);
      }
    } catch (error) {
      console.error('Error creating test:', error);
      if (error.response?.status === 400) {
        alert(error.response.data.error);
      }
    }
  };

  const loadModuleTest = async (moduleId) => {
    try {
      // Получаем все тесты и ищем тест для этого модуля
      const response = await api.get('/tests');
      const test = response.data.find(t => t.module_id === moduleId);

      if (test) {
        setModuleTest(test);
        // Загружаем вопросы теста
        const testDetails = await api.get(`/tests/${test.id}`);
        setTestQuestions(testDetails.data.questions || []);
      } else {
        setModuleTest(null);
        setTestQuestions([]);
      }
    } catch (error) {
      console.error('Error loading module test:', error);
      setModuleTest(null);
      setTestQuestions([]);
    }
  };

  const handleAddQuestion = async () => {
    try {
      console.log('=== ADDING QUESTION FROM FRONTEND ===');
      console.log('Module test:', moduleTest);
      console.log('Question form:', questionForm);

      if (!moduleTest) {
        console.error('No module test selected');
        return;
      }

      // Фильтруем пустые ответы и проверяем, что есть хотя бы один правильный
      const validAnswers = questionForm.answers.filter(a => a.text.trim() !== '');
      console.log('Valid answers:', validAnswers);

      const hasCorrectAnswer = validAnswers.some(a => a.isCorrect);
      console.log('Has correct answer:', hasCorrectAnswer);

      if (!hasCorrectAnswer) {
        alert('Необходимо указать хотя бы один правильный ответ');
        return;
      }

      const requestData = {
        questionText: questionForm.questionText,
        answers: validAnswers
      };
      console.log('Request data:', requestData);

      await api.post(`/tests/${moduleTest.id}/questions`, requestData);

      // Сбрасываем форму
      setQuestionForm({
        questionText: '',
        answers: [
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
          { text: '', isCorrect: false },
          { text: '', isCorrect: false }
        ]
      });

      setEditTestDialog(false);

      // Перезагружаем вопросы
      loadModuleTest(selectedModule.id);
    } catch (error) {
      console.error('Error adding question:', error);
      alert('Ошибка при добавлении вопроса');
    }
  };

  const getContentTypeIcon = (contentType) => {
    switch (contentType) {
      case 'video': return <VideoLibrary />;
      case 'document': return <Description />;
      case 'text': return <Description />;
      default: return <Description />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Кабинет автора курсов
          </Typography>
          <Button variant="outlined" onClick={logout}>
            Выйти
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Список курсов */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Мои курсы</Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setCreateCourseDialog(true)}
                >
                  Создать
                </Button>
              </Box>

              <List>
                {courses.map((course) => (
                  <ListItem
                    key={course.id}
                    button
                    selected={selectedCourse?.id === course.id}
                    onClick={() => {
                      setSelectedCourse(course);
                      loadCourseModules(course.id);
                      setSelectedModule(null);
                      setModuleContent([]);
                    }}
                  >
                    <ListItemText
                      primary={course.title}
                      secondary={`${course.is_published ? 'Опубликован' : 'Черновик'}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCourse(course);
                          setCourseForm({ title: course.title, description: course.description });
                          setEditCourseDialog(true);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTogglePublish(course.id, course.is_published);
                        }}
                      >
                        {course.is_published ? <Unpublished fontSize="small" /> : <Publish fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          loadCourseReport(course.id);
                        }}
                        disabled={loadingReport}
                      >
                        <Assessment fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteCourse(course.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Рабочая область */}
          <Grid item xs={12} md={8}>
            {selectedCourse ? (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedCourse.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedCourse.description}
                </Typography>

                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
                  <Tab label="Модули" />
                  <Tab label="Контент" />
                  <Tab label="Тесты" />
                </Tabs>

                {tabValue === 0 && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1">Модули курса</Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => setCreateModuleDialog(true)}
                      >
                        Добавить модуль
                      </Button>
                    </Box>

                    <List>
                      {courseModules.map((module) => (
                        <ListItem
                          key={module.id}
                          button
                          selected={selectedModule?.id === module.id}
                          onClick={() => {
                            setSelectedModule(module);
                            loadModuleContent(selectedCourse.id, module.id);
                            loadModuleTest(module.id);
                            setTabValue(1);
                          }}
                        >
                          <ListItemText
                            primary={module.title}
                            secondary={`${module.content_count || 0} элементов контента`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedModule(module);
                                setModuleForm({ title: module.title, description: module.description });
                                setEditModuleDialog(true);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteModule(module.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {tabValue === 1 && selectedModule && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1">Контент модуля: {selectedModule.title}</Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => setCreateContentDialog(true)}
                      >
                        Добавить контент
                      </Button>
                    </Box>

                    <List>
                      {moduleContent.map((content) => (
                        <ListItem key={content.id}>
                          <ListItemText
                            primary={content.title}
                            secondary={`Тип: ${content.content_type}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton size="small" onClick={() => handleOpenEditContent(content)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteContent(content.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {tabValue === 2 && selectedModule && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1">Тест модуля: {selectedModule.title}</Typography>
                      {!moduleTest ? (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Add />}
                          onClick={() => setCreateTestDialog(true)}
                        >
                          Создать тест
                        </Button>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Edit />}
                            onClick={() => setEditTestDialog(true)}
                          >
                            Редактировать тест
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => handleDeleteTest(moduleTest.id)}
                          >
                            Удалить тест
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {moduleTest ? (
                      <Box>
                        <Paper sx={{ p: 2, mb: 2 }}>
                          <Typography variant="h6" gutterBottom>
                            {moduleTest.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {moduleTest.description}
                          </Typography>
                          <Typography variant="body2">
                            Проходной балл: {moduleTest.passing_score}% | Вопросов: {testQuestions.length}
                          </Typography>
                        </Paper>

                        <Typography variant="subtitle1" gutterBottom>
                          Вопросы теста
                        </Typography>

                        {testQuestions.length > 0 ? (
                          <List>
                            {testQuestions.map((question, index) => (
                              <ListItem key={question.id} divider>
                                <ListItemText
                                  primary={`${index + 1}. ${question.question_text}`}
                                  secondary={`Вариантов ответа: ${question.answers?.length || 0}`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            В тесте еще нет вопросов.
                          </Typography>
                        )}

                        <Box mt={2}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => setEditTestDialog(true)}
                          >
                            Добавить вопрос
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Для этого модуля тест еще не создан.
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <LibraryBooks sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Выберите курс для редактирования
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Или создайте новый курс, нажав кнопку "Создать"
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Диалоги */}
        {/* Создание курса */}
        <Dialog open={createCourseDialog} onClose={() => setCreateCourseDialog(false)}>
          <DialogTitle>Создать новый курс</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Название курса"
              fullWidth
              value={courseForm.title}
              onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Описание курса"
              fullWidth
              multiline
              rows={3}
              value={courseForm.description}
              onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateCourseDialog(false)}>Отмена</Button>
            <Button onClick={handleCreateCourse} variant="contained">Создать</Button>
          </DialogActions>
        </Dialog>

        {/* Редактирование курса */}
        <Dialog open={editCourseDialog} onClose={() => setEditCourseDialog(false)}>
          <DialogTitle>Редактировать курс</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Название курса"
              fullWidth
              value={courseForm.title}
              onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Описание курса"
              fullWidth
              multiline
              rows={3}
              value={courseForm.description}
              onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditCourseDialog(false)}>Отмена</Button>
            <Button onClick={handleUpdateCourse} variant="contained">Сохранить</Button>
          </DialogActions>
        </Dialog>

        {/* Создание модуля */}
        <Dialog open={createModuleDialog} onClose={() => setCreateModuleDialog(false)}>
          <DialogTitle>Создать модуль</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Название модуля"
              fullWidth
              value={moduleForm.title}
              onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Описание модуля"
              fullWidth
              multiline
              rows={3}
              value={moduleForm.description}
              onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateModuleDialog(false)}>Отмена</Button>
            <Button onClick={handleCreateModule} variant="contained">Создать</Button>
          </DialogActions>
        </Dialog>

        {/* Редактирование модуля */}
        <Dialog open={editModuleDialog} onClose={() => setEditModuleDialog(false)}>
          <DialogTitle>Редактировать модуль</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Название модуля"
              fullWidth
              value={moduleForm.title}
              onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Описание модуля"
              fullWidth
              multiline
              rows={3}
              value={moduleForm.description}
              onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditModuleDialog(false)}>Отмена</Button>
            <Button onClick={handleUpdateModule} variant="contained">Сохранить</Button>
          </DialogActions>
        </Dialog>

        {/* Создание контента */}
        <Dialog open={createContentDialog} onClose={() => setCreateContentDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Добавить контент</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="dense">
              <InputLabel>Тип контента</InputLabel>
              <Select
                value={contentForm.contentType}
                onChange={(e) => setContentForm({ ...contentForm, contentType: e.target.value })}
              >
                <MenuItem value="text">Текст</MenuItem>
                <MenuItem value="video">Видео</MenuItem>
                <MenuItem value="document">Документ</MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="dense"
              label="Название"
              fullWidth
              value={contentForm.title}
              onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
            />

            {contentForm.contentType === 'text' && (
              <TextField
                margin="dense"
                label="Содержание"
                fullWidth
                multiline
                rows={6}
                value={contentForm.content}
                onChange={(e) => setContentForm({ ...contentForm, content: e.target.value })}
              />
            )}

            {(contentForm.contentType === 'video' || contentForm.contentType === 'document') && (
              <Box sx={{ mt: 2 }}>
                <input
                  accept={contentForm.contentType === 'video' ? 'video/*' : '.pdf,.doc,.docx'}
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                />
                <label htmlFor="file-upload">
                  <Button variant="outlined" component="span" fullWidth>
                    Выбрать файл
                  </Button>
                </label>
                {file && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Выбран файл: {file.name}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateContentDialog(false)}>Отмена</Button>
            <Button onClick={handleCreateContent} variant="contained">Добавить</Button>
          </DialogActions>
        </Dialog>

        {/* Создание теста */}
        <Dialog open={createTestDialog} onClose={() => setCreateTestDialog(false)}>
          <DialogTitle>Создать тест</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Название теста"
              fullWidth
              value={testForm.title}
              onChange={(e) => setTestForm({ ...testForm, title: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Описание теста"
              fullWidth
              multiline
              rows={3}
              value={testForm.description}
              onChange={(e) => setTestForm({ ...testForm, description: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Проходной балл (%)"
              type="number"
              fullWidth
              value={testForm.passingScore}
              onChange={(e) => setTestForm({ ...testForm, passingScore: parseInt(e.target.value) })}
              inputProps={{ min: 0, max: 100 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateTestDialog(false)}>Отмена</Button>
            <Button onClick={handleCreateTest} variant="contained">Создать</Button>
          </DialogActions>
        </Dialog>

        {/* Редактирование контента */}
        <Dialog open={editContentDialog} onClose={() => setEditContentDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Редактировать контент</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="dense">
              <InputLabel>Тип контента</InputLabel>
              <Select
                value={contentEditForm.contentType}
                onChange={(e) => setContentEditForm({ ...contentEditForm, contentType: e.target.value })}
              >
                <MenuItem value="text">Текст</MenuItem>
                <MenuItem value="video">Видео</MenuItem>
                <MenuItem value="document">Документ</MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="dense"
              label="Название"
              fullWidth
              value={contentEditForm.title}
              onChange={(e) => setContentEditForm({ ...contentEditForm, title: e.target.value })}
            />

            {contentEditForm.contentType === 'text' && (
              <TextField
                margin="dense"
                label="Содержание"
                fullWidth
                multiline
                rows={6}
                value={contentEditForm.content}
                onChange={(e) => setContentEditForm({ ...contentEditForm, content: e.target.value })}
              />
            )}

            {(contentEditForm.contentType === 'video' || contentEditForm.contentType === 'document') && (
              <Box sx={{ mt: 2 }}>
                <input
                  accept={contentEditForm.contentType === 'video' ? 'video/*' : '.pdf,.doc,.docx'}
                  style={{ display: 'none' }}
                  id="edit-file-upload"
                  type="file"
                  onChange={(e) => setEditFile(e.target.files[0])}
                />
                <label htmlFor="edit-file-upload">
                  <Button variant="outlined" component="span" fullWidth>
                    Выбрать новый файл
                  </Button>
                </label>
                {editFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Выбран файл: {editFile.name}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditContentDialog(false)}>Отмена</Button>
            <Button onClick={handleUpdateContent} variant="contained">Сохранить</Button>
          </DialogActions>
        </Dialog>

        {/* Добавление вопроса к тесту */}
        <Dialog open={editTestDialog} onClose={() => setEditTestDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {moduleTest ? `Добавить вопрос к тесту "${moduleTest.title}"` : 'Добавить вопрос'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Текст вопроса"
              fullWidth
              multiline
              rows={2}
              value={questionForm.questionText}
              onChange={(e) => setQuestionForm({ ...questionForm, questionText: e.target.value })}
            />

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
              Варианты ответов:
            </Typography>

            {questionForm.answers.map((answer, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  label={`Ответ ${index + 1}`}
                  fullWidth
                  value={answer.text}
                  onChange={(e) => {
                    const newAnswers = [...questionForm.answers];
                    newAnswers[index] = { ...newAnswers[index], text: e.target.value };
                    setQuestionForm({ ...questionForm, answers: newAnswers });
                  }}
                />
                <IconButton
                  color={answer.isCorrect ? 'success' : 'default'}
                  onClick={() => {
                    const newAnswers = [...questionForm.answers];
                    newAnswers[index] = { ...newAnswers[index], isCorrect: !newAnswers[index].isCorrect };
                    setQuestionForm({ ...questionForm, answers: newAnswers });
                  }}
                  sx={{ ml: 1 }}
                >
                  <CheckCircle />
                </IconButton>
                <Typography variant="caption" sx={{ ml: 1, minWidth: 80 }}>
                  {answer.isCorrect ? 'Правильный' : 'Неправильный'}
                </Typography>
              </Box>
            ))}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Отметьте правильные ответы, нажав на иконку галочки рядом с вариантом ответа.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditTestDialog(false)}>Отмена</Button>
            <Button
              onClick={handleAddQuestion}
              variant="contained"
              disabled={!questionForm.questionText.trim() || !questionForm.answers.some(a => a.text.trim() !== '' && a.isCorrect)}
            >
              Добавить вопрос
            </Button>
          </DialogActions>
        </Dialog>

        {/* Диалог отчета по курсу */}
        <Dialog
          open={reportDialog}
          onClose={() => setReportDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Отчет по курсу: {courseReport?.course?.title}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'end' }}>
              <TextField
                label="Дата начала"
                type="date"
                value={reportStartDate}
                onChange={(e) => setReportStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="Дата окончания"
                type="date"
                value={reportEndDate}
                onChange={(e) => setReportEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<Refresh />}
                onClick={() => loadCourseReport(courseReport?.course?.id)}
              >
                Применить
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setReportStartDate('');
                  setReportEndDate('');
                  loadCourseReport(courseReport?.course?.id);
                }}
              >
                Сбросить
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (!courseReport?.students) return;
                  const rows = [
                    ['№', 'ФИО', 'Ключ доступа', 'Дата создания', 'Дата начала', 'Пройдено модулей', 'Всего модулей', 'Прогресс %']
                  ];
                  courseReport.students.forEach((s, idx) => {
                    const fio = s.fullName || '-';
                    const access = s.accessCode || '-';
                    const userCreated = s.userCreated ? new Date(s.userCreated).toLocaleDateString('ru-RU') : '-';
                    const started = s.firstModuleStarted ? new Date(s.firstModuleStarted).toLocaleDateString('ru-RU') : '-';
                    rows.push([
                      String(idx + 1),
                      fio,
                      access,
                      userCreated,
                      started,
                      String(s.modulesCompleted ?? 0),
                      String(s.totalModules ?? 0),
                      String(s.progressPercentage ?? 0)
                    ]);
                  });
                  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
                  const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `course_report_${courseReport?.course?.id}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
              >
                Экспорт в Excel
              </Button>
            </Box>

            {loadingReport ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : courseReport?.students?.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>№</TableCell>
                      <TableCell>ФИО</TableCell>
                      <TableCell>Ключ доступа</TableCell>
                      <TableCell>Дата создания</TableCell>
                      <TableCell>Дата начала</TableCell>
                      <TableCell>Пройдено модулей</TableCell>
                      <TableCell>Прогресс</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {courseReport.students.map((student, index) => (
                      <TableRow key={student.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{student.fullName}</TableCell>
                        <TableCell>{student.accessCode}</TableCell>
                        <TableCell>
                          {student.userCreated ? new Date(student.userCreated).toLocaleDateString('ru-RU') : '-'}
                        </TableCell>
                        <TableCell>
                          {student.firstModuleStarted ? new Date(student.firstModuleStarted).toLocaleDateString('ru-RU') : '-'}
                        </TableCell>
                        <TableCell>{student.modulesCompleted}/{student.totalModules}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              {student.progressPercentage}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={student.progressPercentage}
                              sx={{ width: 60, height: 6 }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                Нет данных о студентах для этого курса
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReportDialog(false)}>Закрыть</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default AuthorDashboard;
